import React, { ComponentType } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import { faFileCertificate } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MARKETING_PAGE_BREAKPOINT_TABLET } from '../../Style/breakpoints';
import { SvgCourseCardLearners } from './images/CourseCardLearners';
import { OL_COLOUR_NEW_PINK } from '../../Style/colours';
import {
  mixinImageFillFrameResponsive,
  MIXIN_IMAGE_FILL_IMG,
  IMAGE_FILL_IMG_FIT_WIDTH,
  MIXIN_FOCUS,
  MIXIN_SCREEN_READER_TEXT,
} from '../../Style/mixins';
import { CourseCardProps } from './types';
import { shorten } from './helpers';
import { Button } from '../Button';

interface ContainerProps {
  clickable: boolean;
}

const Container = styled('div')<ContainerProps>(({ clickable }) => [
  `
  display: flex;
  background: #ffffff;
  border: 1px solid #e6e6ee;
  box-sizing: border-box;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;

  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_TABLET}) {
    min-width: 270px;
  }
`,
  clickable &&
    `
    :hover,
    :focus,
    :focus-within {
      transform: translateY(-5px);
      box-shadow: 0px 8px 50px rgba(0, 0, 0, 0.16);
    }
  `,
]);

const SubContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const ImageContainer = styled('div')({
  ...mixinImageFillFrameResponsive({ width: 6, height: 4 }),

  display: 'flex',
  width: '100%',
  flexGrow: 0,
});

const ImageContainerImage = styled('img')({
  ...MIXIN_IMAGE_FILL_IMG,
  ...IMAGE_FILL_IMG_FIT_WIDTH,
});

const InstitutionContainer = styled('div')`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  position: relative;
  padding: 0 20px;
`;

const InstitutionLogo = styled('div')`
  width: 57px;
  flex-shrink: 0;
  margin-top: -12px;
`;

const InstitutionLogoImage = styled('img')`
  width: 57px;
  height: 57px;
  overflow: hidden;
  border-radius: 4px;
  border: 1px solid #ffffff;
`;

const InstitutionName = styled('div')`
  font-family: Avenir, sans-serif;
  font-style: normal;
  font-weight: 800;
  font-size: 16px;
  line-height: 120%;
  letter-spacing: -0.01em;

  color: #7c8788;

  margin-top: 0;
  margin-left: 12px;
`;

const DescriptionContainer = styled('div')`
  display: none;
  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_TABLET}) {
    display: block;
    font-family: Avenir, sans-serif;
    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 140%;
    color: #3e4b4c;
    padding: 12px 0;
  }
`;

const HeaderContainer = styled.h4`
  font-family: Avenir, sans-serif;
  font-style: normal;
  font-weight: 900;
  margin: 5px 0 6px 0;
  font-size: 18px;
  line-height: 120%;
  letter-spacing: -0.01em;
  color: #041416;
  padding-top: 0;

  font-size: 22px;
  padding-top: 10px;
`;

const LearnerContainer = styled('div')({
  display: 'flex',
  marginTop: 10,
});

const Container2 = styled('div')`
  padding: 0 20px;
  margin-bottom: 26px;

  padding-left: 20px;
  margin-bottom: 0px;
`;

const Container3 = styled('div')`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  flex-grow: 0;
  padding: 0 20px 20px 20px;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;

  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_TABLET}) {
    flex-direction: row;
    align-items: center;
  }
`;

const ContentContainer = styled('div')`
  width: 100%;
  position: relative;
  flex-grow: 1;
`;

const LearnersText = styled('div')`
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 140%;
  color: #7c8788;
  margin-left: 12px;
`;

const OpenCredContainer = styled('div')({
  display: 'flex',
  marginTop: 10,
});

const OpenCredsText = styled('div')({
  fontStyle: 'normal',
  fontWeight: 'normal',
  fontSize: '16px',
  lineHeight: '140%',
  color: '#3E4B4C',
  marginLeft: '6px',
});

const OpenCredsIcon = styled('div')({
  width: '24px',
  height: '24px',
  borderRadius: '12px',
  textAlign: 'center',
  flexShrink: 0,
  /* Gradient */
  background:
    'linear-gradient(338.27deg, #E14546 2.4%, #CA2E2F 2.41%, #D20B70 80.44%)',
});

const CourseStatusLinkStyle = {
  borderRadius: 100,
  boxShadow: 'none !important',
  padding: '10px 20px',
  fontFamily: 'Avenir',
  fontStyle: 'normal',
  fontWeight: 900,
  fontSize: 16,
  lineHeight: 1.2,
  textDecoration: 'none',
  color: OL_COLOUR_NEW_PINK,
  border: `1px solid ${OL_COLOUR_NEW_PINK}`,
  margin: '20px 0px 0px 0px',

  '&:hover': {
    color: OL_COLOUR_NEW_PINK,
  },

  '&:focus': MIXIN_FOCUS,

  [`@media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_TABLET})`]: {
    marginTop: 10,
  },
};

const CourseStatus = styled('span')(CourseStatusLinkStyle);

const ClickableArea = styled(Button)({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  margin: 0,
  borderRadius: 0,
  background: 'rgba(0, 0, 0, 0)',
  boxShadow: 'none',
});

const HiddentText = styled('span')(MIXIN_SCREEN_READER_TEXT);

// add comma every three digits (e.g. 1354 -> 1,354)
const formatStudentNumber = (number: number): string =>
  number.toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');

export const CourseCard: ComponentType<CourseCardProps> = ({
  name,
  courseSummary,
  courseType,
  alreadyStarted,
  creator,
  students,
  image,
  startDateISO,
  courseUrl,
  to,
  className,
  hideStatus = false,
  showLearners = false,
  hideStudentCount = false,
  clickable = true,
  institution,
  metricsLogEvent,
}) => {
  const { 'avatar@3x': avatar, fullName } = creator || {};

  const showStudents =
    ((students && students > 0) || showLearners) && !hideStudentCount;
  const enrolText = 'Enrol';

  let startDate = 'On now';

  if (startDateISO) {
    const momentDate = moment(startDateISO);
    if (momentDate.isValid() && momentDate.isAfter()) {
      startDate = enrolText;
    }
  } else if (alreadyStarted === false) {
    startDate = enrolText;
  }

  const logClickEvent = (): void => {
    if (metricsLogEvent)
      metricsLogEvent('FindOutMoreAboutCourse-Clicked', {
        coursePath: courseUrl,
        institutionPath: institution || null,
      });
  };

  return (
    <Container className={className} clickable={clickable}>
      <SubContainer>
        <ImageContainer>
          <ImageContainerImage
            src={image}
            alt="background"
            className="desktop"
          />
        </ImageContainer>
        <ContentContainer>
          <InstitutionContainer>
            <InstitutionLogo>
              <InstitutionLogoImage src={avatar} alt="" aria-hidden="true" />
            </InstitutionLogo>
            <InstitutionName>{fullName}</InstitutionName>
          </InstitutionContainer>
          <Container2>
            <HeaderContainer>{name}</HeaderContainer>
            <DescriptionContainer>
              {shorten(courseSummary, 180)}
            </DescriptionContainer>
          </Container2>
        </ContentContainer>
        <Container3>
          {courseType === 'OPENCRED' ? (
            <OpenCredContainer>
              <OpenCredsIcon>
                <FontAwesomeIcon
                  icon={faFileCertificate}
                  color="white"
                  fixedWidth
                />
              </OpenCredsIcon>
              <OpenCredsText>OpenCred</OpenCredsText>
            </OpenCredContainer>
          ) : (
            <LearnerContainer>
              {showStudents && (
                <>
                  <SvgCourseCardLearners />
                  <LearnersText>
                    {formatStudentNumber(students || 0)} learners
                  </LearnersText>
                </>
              )}
            </LearnerContainer>
          )}
          {hideStatus ? null : <CourseStatus>{startDate}</CourseStatus>}
        </Container3>
      </SubContainer>
      {clickable && (
        <ClickableArea
          variant="tertiary"
          action={
            to
              ? {
                  kind: 'internal_link',
                  value: {
                    link: { to, onClick: logClickEvent },
                  },
                }
              : {
                  kind: 'external_link',
                  value: courseUrl,
                }
          }
        >
          <HiddentText>Go to {name}</HiddentText>
        </ClickableArea>
      )}
    </Container>
  );
};
